import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

export type Priority = "low" | "medium" | "high";
export type TaskStatus = "todo" | "in-progress" | "done";

export interface Task {
  id: string;
  title: string;
  priority: Priority;
  status: TaskStatus;
  comments: number;
  attachments: number;
  tag?: string;
  createdAt: number;
}

export const PRIORITY_CONFIG: Record<Priority, { label: string; color: string; bg: string }> = {
  low:    { label: "Basse",    color: "text-emerald-400",  bg: "bg-emerald-500/10 border-emerald-500/30"  },
  medium: { label: "Moyenne",  color: "text-amber-400",   bg: "bg-amber-500/10 border-amber-500/30"   },
  high:   { label: "Haute",    color: "text-rose-400",    bg: "bg-rose-500/10 border-rose-500/30"     },
};

export const STATUS_COLS: Record<TaskStatus, { label: string; color: string }> = {
  todo:         { label: "À faire",     color: "text-[#A1A1AA]" },
  "in-progress": { label: "En cours",   color: "text-brand-accent" },
  done:         { label: "Terminées",   color: "text-emerald-400" },
};

const STORAGE_KEY = "flexfit_admin_tasks";

// ── Field mapping: internal ↔ Supabase ───────────────────────────────────────
const PRIORITY_MAP: Record<string, Priority> = {
  Haute: "high", Moyenne: "medium", Basse: "low",
  high: "high", medium: "medium", low: "low",
};
const STATUS_MAP: Record<string, TaskStatus> = {
  a_faire: "todo", en_cours: "in-progress", done: "done",
  todo: "todo", "in-progress": "in-progress",
};
const PRIORITY_TO_SB: Record<Priority, string> = {
  high: "Haute", medium: "Moyenne", low: "Basse",
};
const STATUS_TO_SB: Record<TaskStatus, string> = {
  todo: "a_faire", "in-progress": "en_cours", done: "done",
};

interface TasksContextValue {
  tasks: Task[];
  loading: boolean;
  addTask: (task: Omit<Task, "id" | "createdAt" | "comments" | "attachments" | "status">) => void;
  updateTask: (task: Task) => void;
  deleteTask: (id: string) => void;
  updateTaskStatus: (id: string, status: TaskStatus) => void;
  reorderTasks: (tasks: Task[]) => void;
}

const TasksContext = createContext<TasksContextValue | null>(null);

export function TasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  // Load from Supabase, fall back to localStorage
  useEffect(() => {
    async function load() {
      try {
        const { data, error } = await supabase
          .from("tasks")
          .select("*")
          .order("created_at", { ascending: false });

        if (!error && data && data.length > 0) {
          const mapped: Task[] = data.map((row: any) => ({
            id: row.id,
            title: row.title,
            priority: PRIORITY_MAP[row.priority] ?? "medium",
            status: STATUS_MAP[row.status] ?? "todo",
            comments: row.comments ?? 0,
            attachments: row.attachments ?? 0,
            tag: row.tag ?? undefined,
            createdAt: new Date(row.created_at).getTime(),
          }));
          setTasks(mapped);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(mapped));
        } else {
          // Fall back to localStorage
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            try { setTasks(JSON.parse(stored)); } catch { /* ignore */ }
          }
        }
      } catch {
        // Offline — try localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try { setTasks(JSON.parse(stored)); } catch { /* ignore */ }
        }
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  // Persist to both Supabase + localStorage
  const persistTask = useCallback(async (updated: Task[], toastMsg?: string) => {
    setTasks(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    // Sync each changed task to Supabase
    for (const task of updated) {
      const { id, title, priority, status, comments, attachments, tag } = task;
      const sbPriority = PRIORITY_TO_SB[priority];
      const sbStatus = STATUS_TO_SB[status];
      await supabase.from("tasks").upsert({
        id,
        title,
        priority: sbPriority,
        status: sbStatus,
        comments,
        attachments,
        tag: tag ?? null,
      }, { onConflict: "id" }).then(({ error }) => {
        if (error) console.warn("[TasksContext] Supabase sync error:", error.message);
      });
    }
    if (toastMsg) toast.success(toastMsg);
  }, []);

  const addTask = useCallback((data: Omit<Task, "id" | "createdAt" | "comments" | "attachments" | "status">) => {
    const task: Task = {
      ...data,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      status: "todo",
      comments: 0,
      attachments: 0,
    };
    persistTask([task, ...tasks], "Tâche ajoutée");
  }, [tasks, persistTask]);

  const updateTask = useCallback((task: Task) => {
    persistTask(tasks.map(t => t.id === task.id ? task : t), "Tâche mise à jour");
  }, [tasks, persistTask]);

  const deleteTask = useCallback((id: string) => {
    persistTask(tasks.filter(t => t.id !== id), "Tâche supprimée");
    void supabase.from("tasks").delete().eq("id", id);
  }, [tasks, persistTask]);

  const updateTaskStatus = useCallback((id: string, status: TaskStatus) => {
    persistTask(tasks.map(t => t.id === id ? { ...t, status } : t));
    void supabase.from("tasks").update({ status: STATUS_TO_SB[status] }).eq("id", id);
  }, [tasks]);

  const reorderTasks = useCallback((newTasks: Task[]) => {
    setTasks(newTasks);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newTasks));
    // Sync full list to Supabase
    for (const task of newTasks) {
      void supabase.from("tasks").upsert({
        id: task.id,
        title: task.title,
        priority: PRIORITY_TO_SB[task.priority],
        status: STATUS_TO_SB[task.status],
        comments: task.comments,
        attachments: task.attachments,
        tag: task.tag ?? null,
      }, { onConflict: "id" });
    }
  }, []);

  return (
    <TasksContext.Provider value={{ tasks, loading, addTask, updateTask, deleteTask, updateTaskStatus, reorderTasks }}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error("useTasks must be used within TasksProvider");
  return ctx;
}
